import type { ItineraryItem } from './index';

export enum PricingTier {
    LUXURY = 'LUXURY',
    STANDARD = 'STANDARD',
    BUDGET = 'BUDGET'
}

export enum NationalityType {
    LOCAL = 'LOCAL',
    INTERNATIONAL = 'INTERNATIONAL'
}

export interface Season {
    id: string;
    name: 'Peak' | 'High' | 'Low';
    startMonth: number; // 1-12
    startDay: number;   // 1-31
    endMonth: number;   // 1-12
    endDay: number;     // 1-31
    priceMultiplier: number;
}

export interface AccommodationRate {
    seasonId: string;
    adultRate: number;
    childRate: number;
}

export interface Accommodation {
    id: string;
    name: string;
    seasonalRates: AccommodationRate[];
}

export interface AccommodationPool {
    destinationId: string;
    tier: PricingTier;
    hotels: Accommodation[];
}

export interface TierVariant {
    itinerary: ItineraryItem[];
    inclusions: string[];
    exclusions: string[];
    guideClass: string;
    vehicleType: string;
    transportId?: string;
    guideRankingId?: string;
    accommodationPoolId?: string;
    accommodationPools: AccommodationPool[];
    baseServiceCosts: {
        [key in NationalityType]: {
            adult: number;
            child: number;
        };
    };
}

export interface TieredPackage {
    id: string;
    name: string;
    overview: string;
    gallery: string[];
    destinations: string[]; // Destination IDs
    durationDays: number;
    status: 'DRAFT' | 'READY' | 'PUBLISHED';
    tierVariants: {
        [key in PricingTier]?: TierVariant;
    };
    discountedTierVariants?: {
        [key in PricingTier]?: TierVariant;
    };
    createdAt: string;
    updatedAt: string;
}

export interface PricingContextState {
    nationalityType: NationalityType;
    countryCode: string;
    displayCurrency: string;
    tier: PricingTier;
    travelDates: {
        startDate: string;
        endDate: string;
    } | null;
    seasonId?: string;
    pax: {
        adults: number;
        children: number;
    };
    // Search & Filter fields
    searchQuery?: string;
    isFilterOpen?: boolean;
    duration?: string;
    difficulty?: string;
    budgetRange?: {
        min: number;
        max: number;
    };
    category?: string;
    selectedExtras?: any[];
    useOriginalPricing?: boolean; // When true, ignore discountedTierVariants for WAS price calculation
    paymentPlan: 'full' | 'deposit' | 'installments';
}

export interface PricingBreakdown {
    item: string;
    total: number;
    description: string;
    quantity?: number;
    unitPrice?: number;
    type?: string;
}

export interface FinalPrice {
    total: number;
    subtotal: number;
    currency: string;
    breakdown: PricingBreakdown[];
    accommodationCost: number;
    serviceCost: number;
    seasonMultiplier: number;
    originalTotal?: number;
    isDiscounted?: boolean;
}
